// material ui
import { Box, Tooltip, Typography } from '@mui/material';
// 3 functions that are used to find the type of row. Either a group, a child or a leaf node.
import { isAutogeneratedRow } from '@mui/x-data-grid-premium';

export const visibleRowsLookupWithVisibleGroups = (apiRef, rowsObject) => {
  const rows = Object.keys(rowsObject).map((key) => ({ id: key }));
  const visibleRowsLookup = rows.map((row) => {
    const rowId = row.id;
    return { [rowId]: apiRef.current.state.visibleRowsLookup[rowId] !== false };
  });
  return {
    visibleRowsLookup: Object.fromEntries(
      visibleRowsLookup.map(Object.entries).flat(),
    ),
  };
};

// A group row is a row that has children
export const isGroupRow = (params) => params.rowNode.type === 'group';

// A child row is a row that has a parent
export const isChildRow = (params) =>
  params.rowNode.type === 'leaf' && params.rowNode.depth > 0;

// A leaf row in this context is a group with a single entry and therefore is displayed as a leaf node
export const isLeafRow = (params) =>
  params.rowNode.type === 'leaf' && params.rowNode.depth === 0;

export const displayTime = (time) => {
  if (!time) {
    return '';
  }
  const hours = Math.floor(time);
  let minutes = Math.round((time % 1) * 60);
  if (minutes < 10) {
    minutes = `0${minutes}`;
  }
  return `${hours}:${minutes}`;
};

export const onlyRenderInParent = (params) => {
  if (isGroupRow(params) || isLeafRow(params)) {
    return params.formattedValue;
  } else {
    return null;
  }
};

export const CustomHeaderWithLabel = ({ props, t }) => {
  const field = props.colDef.field;
  const label = t(`columns.${field}.label`);
  const header = t(`columns.${field}.header`);
  const description = t(`columns.${field}.description`);

  return (
    <Tooltip
      title={
        <>
          <Typography variant="body2">{header}</Typography>
          <Typography variant="body2">{description}</Typography>
        </>
      }
      placement="top"
    >
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        width="100%"
        overflow="hidden"
      >
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'flex-end',
            width: '100%',
            overflow: 'hidden',
          }}
        >
          <Typography
            variant="b1"
            fontSize="0.9rem"
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              direction: 'ltr',
              textAlign: 'right',
            }}
          >
            {header}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
            width: '100%',
            overflow: 'hidden',
          }}
        >
          <Typography
            variant="body2"
            fontSize={{ xs: 10, sm: 11, md: 12 }}
            color="gray"
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              direction: 'rtl',
            }}
          >
            {label || '\u00A0'}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

export const calculatePercentiles = (array) => {
  if (!array || array.length === 0) {
    return [];
  }

  const sortedArray = [...array].sort((a, b) => a - b);

  const percentiles = [25, 50, 75, 100];

  const result = [];
  percentiles.forEach((percentile) => {
    const index = Math.ceil((percentile / 100) * sortedArray.length) - 1;
    result.push(sortedArray[index]);
  });
  return result;
};

export const sortComparatorWhenGrouping = (v1, v2) => {
  if (v1 == null && v2 == null) return 0;
  if (v1 == null) return 1;
  if (v2 == null) return -1;
  if (isNaN(v1) && isNaN(v2)) return 0;
  if (isNaN(v1)) return -1;
  if (isNaN(v2)) return 1;
  const value1 = Number(typeof v1 === 'string' ? v1.replace('%', '') : v1);
  const value2 = Number(typeof v2 === 'string' ? v2.replace('%', '') : v2);

  if (value1 > value2) return 1;
  if (value1 < value2) return -1;
  return 0;
};

// material ui icons
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';

export const renderTrend = (params) => {
  if (params.value > 10) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <ArrowDropUpRoundedIcon color="error" />
        {params.formattedValue}
      </Box>
    );
  } else if (params.value > 3) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <ArrowDropUpRoundedIcon color="warning" />
        {params.formattedValue}
      </Box>
    );
  } else if (params.value > -3) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        {params.formattedValue}
      </Box>
    );
  } else if (params.value < -3 && params.value != -100) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <ArrowDropDownRoundedIcon color="success" />
        {params.formattedValue}
      </Box>
    );
  } else {
    return <></>;
  }
};
export const renderTrendOpposite = (params) => {
  if (params.value > 10) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <ArrowDropDownRoundedIcon color="success" />
        {params.formattedValue}
      </Box>
    );
  } else if (params.value > 3) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        {params.formattedValue}
      </Box>
    );
  } else if (params.value > -3) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <ArrowDropUpRoundedIcon color="warning" />
        {params.formattedValue}
      </Box>
    );
  } else if (params.value < -3 && params.value != -100) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <ArrowDropUpRoundedIcon color="error" />
        {params.formattedValue}
      </Box>
    );
  } else {
    return <></>;
  }
};
export const NO_OVERRIDE = Symbol('no-override');
export function resolveMatrikelValue(
  matrikel_key,
  column,
  matrikelAnalysisList,
  analysisType,
) {
  if (!matrikelAnalysisList) return null;
  if (column.field === 'matrikel_key') {
    return NO_OVERRIDE;
  }
  const entry = matrikelAnalysisList.find(
    (x) => x.matrikel_key == matrikel_key,
  );
  if (!entry) return null;
  if (column.field === 'power_total_consumption_year') {
    return entry?.matrikel_power?.total_consumption_year ?? NO_OVERRIDE;
  }
  if (column.field === 'heat_total_consumption_year') {
    return entry?.matrikel_heat?.total_consumption_year ?? NO_OVERRIDE;
  }
  if (column.field === 'water_total_consumption_year') {
    return entry?.matrikel_water?.total_consumption_year ?? NO_OVERRIDE;
  }
  if (column.field === 'power_current_calendar_year_sum') {
    return entry?.matrikel_power?.current_calendar_year_sum ?? NO_OVERRIDE;
  }
  if (column.field === 'heat_current_calendar_year_sum') {
    return entry?.matrikel_heat?.current_calendar_year_sum ?? NO_OVERRIDE;
  }
  if (column.field === 'water_current_calendar_year_sum') {
    return entry?.matrikel_water?.current_calendar_year_sum ?? NO_OVERRIDE;
  }
  if (column.field === 'power_consumptionPrYPrM2') {
    return entry?.matrikel_power?.consumptionPrM2PrY ?? NO_OVERRIDE;
  }
  if (column.field === 'heat_consumptionPrYPrM2') {
    return entry?.matrikel_heat?.consumptionPrM2PrY ?? NO_OVERRIDE;
  }
  if (column.field === 'water_consumptionPrYPrM2') {
    return entry?.matrikel_water?.consumptionPrM2PrY ?? NO_OVERRIDE;
  }
  const cell =
    analysisType === 'combined'
      ? (entry?.[column.field] ??
        entry?.[`matrikel_${analysisType}`]?.[column.field])
      : (entry?.[`matrikel_${analysisType}`]?.[column.field] ??
        entry?.[column.field]);

  return cell ?? NO_OVERRIDE;
}

// v7 signatures first; fallback to legacy params-object if needed
export function runValueGetter(getter, nextValue, row, column, api) {
  if (!getter) return nextValue;
  return getter(nextValue, row, column, api);
}

export function valueGetterWrapper(
  value,
  row,
  column,
  api,
  origValueGetter,
  matrikelAnalysisData,
  analysisType,
) {
  if (!isAutogeneratedRow(row)) {
    return origValueGetter
      ? runValueGetter(origValueGetter, value, row, column, api)
      : (value ?? null);
  }

  let rowId = row && row.id;
  // If row.id is not present, try to extract the id from a symbol property
  if (!rowId && row && typeof row === 'object') {
    const symbolKeys = Object.getOwnPropertySymbols(row);
    for (const sym of symbolKeys) {
      const val = row[sym];
      if (typeof val === 'string' && val.startsWith('auto-generated-row-')) {
        rowId = val;
        break;
      }
    }
  }
  const matrikel_key = typeof rowId === 'string' ? rowId.split('/')[1] : null;

  const override = resolveMatrikelValue(
    matrikel_key,
    column,
    matrikelAnalysisData?.MatrikelAnalysis,
    analysisType,
  );
  if (override === NO_OVERRIDE) {
    return origValueGetter
      ? runValueGetter(origValueGetter, value, row, column, api)
      : (value ?? null);
  }
  const computed = runValueGetter(origValueGetter, override, row, column, api);
  return computed ?? null;
}

export const getOverwrite = (field, data) => {
  if (!data?.OverwriteFlag)
    return { value: data?.[field], isOverwritten: false };
  const value = data?.[`Overwrite${field}`] ?? data?.[field];
  const isOverwritten = data?.[`Overwrite${field}`] !== null;

  return { value, isOverwritten };
};
